import { USER_ROLE } from "Constants/constant";
import {
    capitalizeFirstLetter,
    capitalizeWords,
    classNames,
} from "Utils/utils";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import Breadcrumb from "components/common/Breadcrumb/Breadcrumb";
import CustomButton from "components/common/Button/CustomButton";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import Header from "components/common/Header/Header";
import SideBarNew from "components/common/SideBar/SideBarNew";
import { LoginContext } from "helpers/LoginContext";
import { roleAccess } from "helpers/RolesPermission";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { BiArrowBack } from "react-icons/bi";
import {
    FiAlertCircle,
    FiCheckCircle,
    FiChevronDown,
    FiChevronUp,
    FiClock,
    FiDownload,
    FiEdit,
    FiFileText,
    FiPlusCircle,
    FiTrash2,
    FiUser,
    FiUsers,
} from "react-icons/fi";
import { connect } from "react-redux";
import api from "store/services/incident.services";
import { useNavigate, useParams } from "react-router-dom";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import { incidentActions } from "store/sagas/incident.sagas";
import {
    getIncidentCloseDraftLoading,
    getIncidentCloseDraftSuccess,
    getIncidentCloseLoading,
    getIncidentCloseSuccess,
    getIncidentDetail,
    getIncidentDetailLoading,
    getIncidentInvestigateLoading,
    getIncidentInvestigateSuccess,
} from "store/selector/incident.selector";
import { InvestigationForm, ReviewCloseForm } from "./components/ManagerForms";
import {
  InvestigationViewReadOnly,
  ClosureViewReadOnly,
} from "./components/InvestigationClosureView";
import {
    BODY_PARTS,
    INCIDENT_CATEGORIES,
    INCIDENT_STATUS,
    INJURY_CAUSES,
    MEDICATION_INCIDENT_CATEGORIES,
    OUTCOME_OPTIONS,
    getPersonInvolvedOptions,
    STATUS_BADGES,
} from "./constants";
import {
    formatYesNo,
    getGroupedBodyParts,
    getOptionLabel as getLabel,
} from "./utils/formatters";
import useCareLabels from "hooks/useCareLabels";

const BREADCRUMB = [
  { name: "Incident Management", href: "/incident-management" },
  { name: "View Incident", href: "#" },
];

// UI Components
const Section = ({
  title,
  children,
  className = "",
  collapsible = false,
  defaultOpen = true,
  icon = null,
  onEdit = null,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className={classNames(
        "bg-white rounded-lg shadow-sm border border-gray-100",
        className,
      )}
    >
      <div
        className={classNames(
          "px-5 py-3 border-b border-gray-100 flex items-center justify-between",
          collapsible && "cursor-pointer hover:bg-gray-50 transition-colors",
        )}
        onClick={() => collapsible && setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-gray-400">{icon}</span>}
          <h2 className="text-base font-semibold text-gray-800">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          {onEdit && (
            <button
              type="button"
              title={`Edit ${title}`}
              aria-label={`Edit ${title}`}
              onClick={(event) => {
                event.stopPropagation();
                onEdit();
              }}
              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50"
            >
              <FiEdit className="w-4 h-4" />
            </button>
          )}
          {collapsible && (
            <span className="text-gray-400">
              {isOpen ? (
                <FiChevronUp className="w-5 h-5" />
              ) : (
                <FiChevronDown className="w-5 h-5" />
              )}
            </span>
          )}
        </div>
      </div>
      {(!collapsible || isOpen) && <div className="p-5">{children}</div>}
    </div>
  );
};

const Field = ({ label, value, className = "", inline = false }) => (
  <div
    className={classNames(inline ? "flex items-center gap-2" : "", className)}
  >
    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
      {label}
    </label>
    <p className={classNames("text-sm text-gray-800", !inline && "mt-0.5")}>
      {value || "-"}
    </p>
  </div>
);

const InfoCard = ({ label, value, variant = "default" }) => {
  const variants = {
    default: "bg-gray-50 text-gray-700",
    success: "bg-green-50 text-green-700",
    danger: "bg-red-50 text-red-700",
    warning: "bg-amber-50 text-amber-700",
    info: "bg-blue-50 text-blue-700",
  };
  const formattedValue =
    typeof value === "string" ? capitalizeFirstLetter(value) : value;

  return (
    <div
      className={classNames(
        "px-3 py-2 rounded-lg text-center border",
        variants[variant],
      )}
    >
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold">{formattedValue}</p>
    </div>
  );
};

const Badge = ({
  children,
  color = "gray",
  size = "sm",
  noCapitalize = false,
}) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    red: "bg-red-50 text-red-700 border-red-200",
    yellow: "bg-amber-50 text-amber-700 border-amber-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
  };
  const sizes = {
    xs: "px-2 py-0.5 text-xs",
    sm: "px-2.5 py-1 text-xs",
    md: "px-3 py-1 text-sm",
  };
  const formattedChildren =
    typeof children === "string" && !noCapitalize
      ? capitalizeWords(children)
      : children;

  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border font-medium",
        colors[color],
        sizes[size],
      )}
    >
      {formattedChildren}
    </span>
  );
};

const getActivityIcon = (activityType, metadata = null) => {
  if (activityType === "other" && metadata?.action === "deleted") {
    return <FiTrash2 className="h-4 w-4 text-red-600" />;
  }
  switch (activityType) {
    case "created":
      return <FiPlusCircle className="h-4 w-4 text-green-600" />;
    case "updated":
      return <FiEdit className="h-4 w-4 text-blue-600" />;
    case "submitted":
      return <FiCheckCircle className="h-4 w-4 text-purple-600" />;
    case "deleted":
      return <FiTrash2 className="h-4 w-4 text-red-600" />;
    default:
      return <FiClock className="h-4 w-4 text-gray-600" />;
  }
};

const getActivityBgColor = (activityType, metadata = null) => {
  if (activityType === "other" && metadata?.action === "deleted")
    return "bg-red-100";
  switch (activityType) {
    case "created":
      return "bg-green-100";
    case "updated":
      return "bg-blue-100";
    case "submitted":
      return "bg-purple-100";
    case "deleted":
      return "bg-red-100";
    default:
      return "bg-gray-100";
  }
};

const ViewIncident = (props) => {
  const { participantLabel, participantLabelPlural, careSector } = useCareLabels();
  const personInvolvedOptions = getPersonInvolvedOptions(careSector);
  const {
    incidentDetail,
    detailLoading,
    getIncidentDetailCall,
    investigateIncidentCall,
    investigateLoading,
    investigateSuccess,
    closeIncidentCall,
    closeIncidentDraftCall,
    closeLoading,
    closeSuccess,
    closeDraftLoading,
    closeDraftSuccess,
    resetInvestigateCall,
    resetCloseCall,
    resetCloseDraftCall,
  } = props;

  const { uuid } = useParams();
  const navigate = useNavigate();
  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);

  const [showInvestigateModal, setShowInvestigateModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [activityExpanded, setActivityExpanded] = useState(false);

  const isManager = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]);

  useEffect(() => {
    if (uuid) getIncidentDetailCall(uuid);
  }, [uuid]);

  // Scroll to top when viewing an incident so content displays from top
  useEffect(() => {
    if (uuid) window.scrollTo(0, 0);
  }, [uuid]);

  useEffect(() => {
    if (showInvestigateModal) resetInvestigateCall();
  }, [showInvestigateModal]);

  useEffect(() => {
    if (showCloseModal) resetCloseCall();
    if (showCloseModal) resetCloseDraftCall();
  }, [showCloseModal]);

  useEffect(() => {
    if (investigateSuccess) {
      setShowInvestigateModal(false);
      getIncidentDetailCall(uuid);
      setTimeout(() => resetInvestigateCall(), 100);
    }
  }, [investigateSuccess]);

  useEffect(() => {
    if (closeSuccess) {
      setShowCloseModal(false);
      getIncidentDetailCall(uuid);
      setTimeout(() => resetCloseCall(), 100);
    }
  }, [closeSuccess]);

  useEffect(() => {
    if (closeDraftSuccess) {
      setShowCloseModal(false);
      getIncidentDetailCall(uuid);
      setTimeout(() => resetCloseDraftCall(), 100);
    }
  }, [closeDraftSuccess]);

  const getStatusBadge = (status) => {
    const badge = STATUS_BADGES[status] || STATUS_BADGES.draft;
    return (
      <span
        className={classNames(
          "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium",
          badge.bg,
          badge.text,
        )}
      >
        {badge.label}
      </span>
    );
  };

  if (detailLoading) {
    return (
      <div className="min-h-full">
        <SideBarNew />
        <div
          className={classNames(
            "flex flex-col",
            sideBarPadding
              ? "lg:pl-[4rem] duration-300"
              : "lg:pl-64 duration-300",
          )}
        >
          <main className="flex-1 bg-gray-50 min-h-screen">
            <Header
              title="View Incident"
              breadcrumb={<Breadcrumb pages={BREADCRUMB} />}
            />
            <div className="flex justify-center items-center py-20">
              <CircularLoader />
            </div>
          </main>
        </div>
      </div>
    );
  }

  const hasValidDetail =
    incidentDetail &&
    typeof incidentDetail === "object" &&
    incidentDetail !== null &&
    "id" in incidentDetail &&
    incidentDetail.id != null;

  if (!hasValidDetail) {
    return (
      <div className="min-h-full">
        <SideBarNew />
        <div
          className={classNames(
            "flex flex-col",
            sideBarPadding
              ? "lg:pl-[4rem] duration-300"
              : "lg:pl-64 duration-300",
          )}
        >
          <main className="flex-1 bg-gray-50 min-h-screen">
            <Header
              title="View Incident"
              breadcrumb={<Breadcrumb pages={BREADCRUMB} />}
            />
            <div className="flex flex-col justify-center items-center py-20">
              <FiAlertCircle className="w-16 h-16 text-gray-400 mb-4" />
              <p className="text-gray-500 text-lg">Incident not found</p>
              <CustomButton
                variant="primary"
                label="Back to List"
                clickHandler={() => navigate("/incident-management")}
                cssClass="mt-4"
              />
            </div>
          </main>
        </div>
      </div>
    );
  }

  const parseMetadata = (activity) => {
    if (!activity.metadata) return null;
    try {
      return typeof activity.metadata === "string"
        ? JSON.parse(activity.metadata)
        : activity.metadata;
    } catch {
      return null;
    }
  };

  const canEditThisIncident = (() => {
    const hasEditPermission = roleAccess(
      [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager, USER_ROLE.Sp_Standard_User],
      PERMISSION_KEYS.INCIDENT_MANAGEMENT,
      PERMISSION_KEYS_CHILDREN.INCIDENT_MANAGEMENT.EDIT,
    );
    if (!hasEditPermission) return false;
    const status = incidentDetail.status;
    if (
      status === INCIDENT_STATUS.DRAFT ||
      status === INCIDENT_STATUS.SUBMITTED
    ) {
      return true;
    }
    return (
      isManager &&
      [
        INCIDENT_STATUS.UNDER_INVESTIGATION,
        INCIDENT_STATUS.REPORTED,
        INCIDENT_STATUS.CLOSED,
      ].includes(status)
    );
  })();

  const openEditSection = (stepId) => {
    navigate(`/incident-management/edit/${uuid}`, { state: { stepId } });
  };

  const sectionEdit = (stepId) =>
    canEditThisIncident ? () => openEditSection(stepId) : null;

  return (
    <div className="min-h-full">
      <SideBarNew />
      <div
        className={classNames(
          "flex flex-col",
          sideBarPadding
            ? "lg:pl-[4rem] duration-300"
            : "lg:pl-64 duration-300",
        )}
      >
        <main className="flex-1 bg-gray-50 min-h-screen">
          <Header
            title="View Incident"
            breadcrumb={<Breadcrumb pages={BREADCRUMB} />}
          />

          <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
              <div className="flex items-center gap-4">
                <CustomButton
                  variant="secondary"
                  label="Back"
                  Icon={<BiArrowBack />}
                  clickHandler={() => navigate("/incident-management")}
                />
                <div>
                  <h1 className="text-xl font-semibold text-gray-800">
                    Incident #IN-{incidentDetail.id}
                  </h1>
                  <p className="text-sm text-gray-500">
                    Reported on{" "}
                    {moment(incidentDetail.created_at).format(
                      "DD/MM/YYYY hh:mm A",
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {getStatusBadge(incidentDetail.status)}

                {roleAccess(
                  [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager],
                  PERMISSION_KEYS.INCIDENT_MANAGEMENT,
                  PERMISSION_KEYS_CHILDREN.INCIDENT_MANAGEMENT.INVESTIGATE,
                ) &&
                  [
                    INCIDENT_STATUS.SUBMITTED,
                    INCIDENT_STATUS.CLOSED,
                    INCIDENT_STATUS.REPORTED,
                  ].includes(incidentDetail.status) && (
                    <CustomButton
                      variant="primary"
                      label="Start Investigation"
                      clickHandler={() => setShowInvestigateModal(true)}
                    />
                  )}

                {isManager &&
                  incidentDetail.status ===
                    INCIDENT_STATUS.UNDER_INVESTIGATION && (
                    <>
                      {roleAccess(
                        [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager],
                        PERMISSION_KEYS.INCIDENT_MANAGEMENT,
                        PERMISSION_KEYS_CHILDREN.INCIDENT_MANAGEMENT
                          .INVESTIGATE,
                      ) && (
                        <CustomButton
                          variant="secondary"
                          label="Edit Investigation"
                          clickHandler={() => setShowInvestigateModal(true)}
                        />
                      )}
                      {roleAccess(
                        [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager],
                        PERMISSION_KEYS.INCIDENT_MANAGEMENT,
                        PERMISSION_KEYS_CHILDREN.INCIDENT_MANAGEMENT.CLOSE,
                      ) && (
                        <CustomButton
                          variant="primary"
                          label="Review & Close"
                          clickHandler={() => setShowCloseModal(true)}
                        />
                      )}
                    </>
                  )}
              </div>
            </div>

            {(() => {
              const isMedication =
                incidentDetail.outcome_type === "medication";
              return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              <div
                className={
                  isMedication ? "lg:col-span-3" : "lg:col-span-2"
                }
              >
                <Section
                  title="Basic Details"
                  className="h-full mb-0"
                  onEdit={sectionEdit("basic_details")}
                >
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <Field
                      label="Date"
                      value={moment(incidentDetail.incident_date).format(
                        "DD/MM/YYYY",
                      )}
                    />
                    <Field
                      label="Time"
                      value={
                        incidentDetail.incident_time
                          ? (() => {
                              const parsed = moment(
                                incidentDetail.incident_time,
                                "HH:mm",
                              );
                              return parsed.isValid()
                                ? parsed.format("hh:mm A")
                                : incidentDetail.incident_time;
                            })()
                          : "-"
                      }
                    />
                    <Field
                      label="Type"
                      value={getLabel(
                        OUTCOME_OPTIONS,
                        incidentDetail.outcome_type,
                      )}
                    />
                    <Field
                      label="Reported By"
                      value={
                        incidentDetail.reported_by?.name ||
                        incidentDetail.created_by?.name
                      }
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1 mb-2">
                        <FiUsers className="w-3 h-3" /> {participantLabelPlural}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {incidentDetail.participants?.map((p) => (
                          <Badge key={p.id} color="blue" size="xs">
                            {capitalizeWords(
                              `${p.first_name || ""} ${p.last_name || ""}`.trim(),
                            )}
                          </Badge>
                        ))}
                        {!incidentDetail.participants?.length && (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </div>
                    </div>
                    {incidentDetail.workers?.length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1 mb-2">
                          <FiUser className="w-3 h-3" /> Workers Involved
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {incidentDetail.workers?.map((w) => (
                            <Badge key={w?.id} color="purple" size="xs">
                              {capitalizeWords(
                                `${w?.first_name || ""} ${w?.last_name || ""}`.trim(),
                              )}
                            </Badge>
                          )) || []}
                        </div>
                      </div>
                    )}
                  </div>
                </Section>
              </div>

              {!isMedication && (
                <div className="lg:col-span-1">
                  <Section
                    title="Response Summary"
                    className="h-full mb-0"
                    onEdit={sectionEdit("incident_overview")}
                  >
                    <div className="grid grid-cols-2 gap-2">
                      <InfoCard
                        label="First Aid"
                        value={formatYesNo(incidentDetail.first_aid_given)}
                        variant={
                          incidentDetail.first_aid_given === "yes"
                            ? "success"
                            : "default"
                        }
                      />
                      <InfoCard
                        label="Ambulance"
                        value={formatYesNo(incidentDetail.ambulance_needed)}
                        variant={
                          incidentDetail.ambulance_needed === "yes"
                            ? "danger"
                            : "default"
                        }
                      />
                      <InfoCard
                        label="Police"
                        value={formatYesNo(incidentDetail.reported_to_police)}
                        variant={
                          incidentDetail.reported_to_police === "yes"
                            ? "info"
                            : "default"
                        }
                      />
                      <InfoCard
                        label="Injuries"
                        value={incidentDetail.injuries_occurred ? "Yes" : "No"}
                        variant={
                          incidentDetail.injuries_occurred
                            ? "danger"
                            : "success"
                        }
                      />
                      <InfoCard
                        label="PRN Given"
                        value={formatYesNo(incidentDetail.prn_administered)}
                        variant={
                          incidentDetail.prn_administered === "yes"
                            ? "warning"
                            : "default"
                        }
                      />
                      <InfoCard
                        label="Medical"
                        value={formatYesNo(
                          incidentDetail.medical_review_offered,
                        )}
                        variant={
                          incidentDetail.medical_review_offered === "yes"
                            ? "success"
                            : "default"
                        }
                      />
                    </div>
                  </Section>
                </div>
              )}
            </div>
              );
            })()}

            {incidentDetail.outcome_type !== "medication" && (
            <Section
              title="Incident Overview"
              className="mb-4"
              onEdit={sectionEdit("incident_overview")}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Field
                  label="Location"
                  value={
                    incidentDetail.incident_address || incidentDetail.location
                  }
                />
                <Field
                  label="Location Details"
                  value={incidentDetail.incident_location_details}
                />
              </div>

              {incidentDetail.incident_category &&
                Array.isArray(incidentDetail.incident_category) &&
                incidentDetail.incident_category.length > 0 && (
                  <div className="mb-3">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
                      Incident Category
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {incidentDetail.incident_category.map((cat, idx) => (
                        <Badge key={idx} color="yellow" size="xs">
                          {getLabel(INCIDENT_CATEGORIES, cat)}
                        </Badge>
                      ))}
                    </div>
                    {incidentDetail.incident_category_others && (
                      <p className="mt-1 text-xs text-gray-500">
                        Other: {incidentDetail.incident_category_others}
                      </p>
                    )}
                  </div>
                )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(incidentDetail.person_involved &&
                  Array.isArray(incidentDetail.person_involved) &&
                  incidentDetail.person_involved.length > 0) ||
                incidentDetail.person_involved_visitor ||
                incidentDetail.person_involved_volunteer ||
                incidentDetail.person_involved_other ||
                incidentDetail.person_involved_others ? (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
                      Persons Involved
                    </label>
                    {incidentDetail.person_involved &&
                      Array.isArray(incidentDetail.person_involved) &&
                      incidentDetail.person_involved.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {incidentDetail.person_involved.map(
                            (person, idx) => (
                              <Badge key={idx} color="gray" size="xs">
                                {getLabel(personInvolvedOptions, person)}
                              </Badge>
                            ),
                          )}
                        </div>
                      )}
                    {(incidentDetail.person_involved_visitor ||
                      incidentDetail.person_involved_volunteer ||
                      incidentDetail.person_involved_other ||
                      incidentDetail.person_involved_others) && (
                      <div
                        className={
                          incidentDetail.person_involved?.length > 0
                            ? "mt-3 space-y-2"
                            : "space-y-2"
                        }
                      >
                        {incidentDetail.person_involved_visitor && (
                          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Visitor
                            </span>
                            <p className="mt-0.5 text-sm text-gray-800">
                              {incidentDetail.person_involved_visitor}
                            </p>
                          </div>
                        )}
                        {incidentDetail.person_involved_volunteer && (
                          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Volunteer
                            </span>
                            <p className="mt-0.5 text-sm text-gray-800">
                              {incidentDetail.person_involved_volunteer}
                            </p>
                          </div>
                        )}
                        {(incidentDetail.person_involved_other ||
                          incidentDetail.person_involved_others) && (
                          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Other
                            </span>
                            <p className="mt-0.5 text-sm text-gray-800">
                              {incidentDetail.person_involved_other ||
                                incidentDetail.person_involved_others}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}
                <Field
                  label={`Has the person responsible for the ${participantLabel.toLowerCase()} been notified?`}
                  value={formatYesNo(incidentDetail.person_responsible_notified)}
                />
              </div>
            </Section>
            )}

            {(incidentDetail.executive_summary ||
              incidentDetail.incident_details_narrative ||
              incidentDetail.conclusion) && (
              <Section
                title="Incident Details"
                className="mb-4"
                onEdit={sectionEdit("incident_details")}
              >
                {incidentDetail.executive_summary && (
                  <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r">
                    <label className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-1 block">
                      Summarise the incident and/or allegation
                    </label>
                    <div className="incident-view-scroll">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                        {incidentDetail.executive_summary}
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <InfoCard
                    label="Does this person have a behaviour management plan?"
                    value={formatYesNo(
                      incidentDetail.has_behavior_management_plan,
                    )}
                    variant={
                      incidentDetail.has_behavior_management_plan
                        ? "info"
                        : "default"
                    }
                  />
                  <InfoCard
                    label="Were any regulated restrictive practices used?"
                    value={formatYesNo(
                      incidentDetail.regulated_restrictive_practices_used,
                    )}
                    variant={
                      incidentDetail.regulated_restrictive_practices_used
                        ? "warning"
                        : "default"
                    }
                  />
                </div>

                {incidentDetail.current_task && (
                  <div className="mb-4 bg-gray-50 p-3 rounded-lg">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                      What task was being performed or what the {participantLabel.toLowerCase()} was
                      doing at the time of the incident?
                    </label>
                    <div className="incident-view-scroll">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                        {incidentDetail.current_task}
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {incidentDetail.incident_details_narrative && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                        What happened? (The details of the incident)
                      </label>
                      <div className="incident-view-scroll">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">
                          {incidentDetail.incident_details_narrative}
                        </p>
                      </div>
                    </div>
                  )}

                  {incidentDetail.triggers_circumstances && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                        What were the circumstances leading up to the incident? Are there any triggers?
                      </label>
                      <div className="incident-view-scroll">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">
                          {incidentDetail.triggers_circumstances}
                        </p>
                      </div>
                    </div>
                  )}

                  {incidentDetail.immediate_action_taken && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                        What immediate action was taken?
                      </label>
                      <div className="incident-view-scroll">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">
                          {incidentDetail.immediate_action_taken}
                        </p>
                      </div>
                    </div>
                  )}

                  {incidentDetail.conclusion && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                        How did the incident end?
                      </label>
                      <div className="incident-view-scroll">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">
                          {incidentDetail.conclusion}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {incidentDetail.incident_details_witnesses &&
                  Array.isArray(incidentDetail.incident_details_witnesses) &&
                  incidentDetail.incident_details_witnesses.length > 0 && (
                    <div className="mt-4">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
                        Witnesses (
                        {incidentDetail.incident_details_witnesses.length})
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {incidentDetail.incident_details_witnesses.map(
                          (w, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg border border-gray-100"
                            >
                              <FiUser className="text-gray-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <span className="font-medium text-gray-800">
                                  {capitalizeWords(
                                    `${w.first_name || w.name || ""} ${w.surname || ""}`.trim(),
                                  )}
                                </span>
                                {(w.date_of_birth ||
                                  w.gender ||
                                  w.contact ||
                                  w.address) && (
                                  <p className="text-xs text-gray-500 truncate">
                                    {[
                                      w.date_of_birth &&
                                        `DOB: ${moment(w.date_of_birth).format("DD/MM/YYYY")}`,
                                      w.gender &&
                                        capitalizeFirstLetter(w.gender),
                                      w.contact,
                                      w.address,
                                    ]
                                      .filter(Boolean)
                                      .join(" • ")}
                                  </p>
                                )}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}
              </Section>
            )}

            {incidentDetail.injuries_occurred && (
              <Section
                title="Injury Information"
                className="mb-4"
                onEdit={sectionEdit("injury_form")}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="space-y-3">
                    <div className="bg-red-50 p-3 rounded-lg border border-red-100">
                      <label className="text-xs font-medium text-red-700 uppercase tracking-wide mb-1 block">
                        Injury Cause
                      </label>
                      <div className="incident-view-scroll">
                        <div className="flex flex-wrap gap-1.5">
                          {Array.isArray(incidentDetail.injury_cause) ? (
                            incidentDetail.injury_cause.map((cause, idx) => (
                              <Badge key={idx} color="red" size="xs">
                                {getLabel(INJURY_CAUSES, cause)}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-gray-800">
                              {getLabel(
                                INJURY_CAUSES,
                                incidentDetail.injury_cause,
                              )}
                            </span>
                          )}
                        </div>
                        {incidentDetail.injury_cause_other && (
                          <p className="mt-1 text-xs text-red-600">
                            Other: {incidentDetail.injury_cause_other}
                          </p>
                        )}
                      </div>
                    </div>

                    {incidentDetail.abuse_check && (
                      <div
                        className={classNames(
                          "p-3 rounded-lg border",
                          incidentDetail.abuse_check === "yes"
                            ? "bg-amber-50 border-amber-200"
                            : "bg-gray-50 border-gray-100",
                        )}
                      >
                        <label className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-1 block">
                            Does the {participantLabel.toLowerCase()} look like there has been abuse/ neglect/ exploitation/ discrimination.
                        </label>
                        <div className="incident-view-scroll">
                          <p
                            className={classNames(
                              "text-sm font-medium",
                              incidentDetail.abuse_check === "yes"
                                ? "text-amber-800"
                                : "text-gray-600",
                            )}
                          >
                            {incidentDetail.abuse_check === "yes"
                              ? "Yes - Suspected"
                              : formatYesNo(incidentDetail.abuse_check)}
                          </p>
                          {incidentDetail.abuse_check === "yes" &&
                            incidentDetail.abuse_details && (
                              <p className="mt-1 text-xs text-amber-700">
                                {incidentDetail.abuse_details}
                              </p>
                            )}
                        </div>
                      </div>
                    )}

                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                        Treatment Administered
                      </label>
                      <p className="text-sm text-gray-800">
                        {formatYesNo(incidentDetail.treatment_administered)}
                      </p>
                      {incidentDetail.treatment_administered === "yes" &&
                        incidentDetail.treatment_administrator && (
                          <p className="text-xs text-gray-500 mt-1">
                            By: {incidentDetail.treatment_administrator}
                          </p>
                        )}
                    </div>

                    {incidentDetail.injury_details && (
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                          Specify (additional details about injury location)
                        </label>
                        <div className="incident-view-scroll">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">
                            {incidentDetail.injury_details}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {incidentDetail.body_parts &&
                    Array.isArray(incidentDetail.body_parts) &&
                    incidentDetail.body_parts.length > 0 && (
                      <div className="lg:col-span-2">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
                          Body Parts Affected (
                          {incidentDetail.body_parts.length})
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {(() => {
                            const groupedParts = getGroupedBodyParts(
                              incidentDetail.body_parts,
                              BODY_PARTS,
                            );
                            return groupedParts.map((group, idx) => (
                              <div
                                key={idx}
                                className="bg-red-50 p-2.5 rounded-lg border border-red-100"
                              >
                                <p className="text-xs font-semibold text-red-800 mb-1.5">
                                  {group.groupLabel}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {group.parts.map((part, pIdx) => (
                                    <span
                                      key={pIdx}
                                      className="text-xs bg-white text-red-700 px-2 py-0.5 rounded border border-red-200"
                                    >
                                      {part}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    )}
                </div>
              </Section>
            )}

            {incidentDetail.outcome_type === "medication" && (
              <Section
                title="Medication Incident Details"
                className="mb-4"
                onEdit={sectionEdit("medication_form")}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="bg-amber-50 p-3 rounded-lg border border-amber-100">
                      <label className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-1.5 block">
                        Categories
                      </label>
                      {incidentDetail.medication_incident_categories &&
                      Array.isArray(
                        incidentDetail.medication_incident_categories,
                      ) &&
                      incidentDetail.medication_incident_categories.length >
                        0 ? (
                        <>
                          <div className="incident-view-scroll">
                            <div className="flex flex-wrap gap-1.5">
                              {incidentDetail.medication_incident_categories.map(
                                (cat, idx) => (
                                  <Badge key={idx} color="yellow" size="xs">
                                    {getLabel(
                                      MEDICATION_INCIDENT_CATEGORIES,
                                      cat,
                                    )}
                                  </Badge>
                                ),
                              )}
                            </div>
                            {incidentDetail.medication_other_description && (
                              <p className="mt-1.5 text-xs text-amber-600">
                                Other:{" "}
                                {incidentDetail.medication_other_description}
                              </p>
                            )}
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          No category specified
                        </p>
                      )}
                    </div>

                    {incidentDetail.medication_possible_reasons && (
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                          Possible Reasons
                        </label>
                        <div className="incident-view-scroll">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">
                            {incidentDetail.medication_possible_reasons}
                          </p>
                        </div>
                      </div>
                    )}

                    {incidentDetail.medication_immediate_action && (
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                          Immediate Action
                        </label>
                        <div className="incident-view-scroll">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">
                            {incidentDetail.medication_immediate_action}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
                      Notifications
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <InfoCard
                        label="Coordinator"
                        value={formatYesNo(incidentDetail.coordinator_notified)}
                        variant={
                          incidentDetail.coordinator_notified === "yes" ||
                          incidentDetail.coordinator_notified === true
                            ? "success"
                            : "default"
                        }
                      />
                      <InfoCard
                        label="Doctor"
                        value={formatYesNo(incidentDetail.doctor_notified)}
                        variant={
                          incidentDetail.doctor_notified === "yes" ||
                          incidentDetail.doctor_notified === true
                            ? "success"
                            : "default"
                        }
                      />
                      <InfoCard
                        label="Pharmacist"
                        value={formatYesNo(incidentDetail.pharmacist_notified)}
                        variant={
                          incidentDetail.pharmacist_notified === "yes" ||
                          incidentDetail.pharmacist_notified === true
                            ? "success"
                            : "default"
                        }
                      />
                    </div>
                  </div>
                </div>
              </Section>
            )}

            {/* Investigation – Manager/SP Admin only (under_investigation, closed, reported). Blue portal excluded. */}
            {(() => {
              const canShowInvestigation =
                roleAccess(
                  [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager],
                  PERMISSION_KEYS.INCIDENT_MANAGEMENT,
                  PERMISSION_KEYS_CHILDREN.INCIDENT_MANAGEMENT.INVESTIGATE,
                ) &&
                [
                  INCIDENT_STATUS.UNDER_INVESTIGATION,
                  INCIDENT_STATUS.CLOSED,
                  INCIDENT_STATUS.REPORTED,
                ].includes(incidentDetail?.status) &&
                incidentDetail?.investigation?.data;
              if (!canShowInvestigation) return null;
              return (
                <Section
                  title="Investigation"
                  className="mb-4"
                  icon={<FiFileText className="w-4 h-4" />}
                >
                  <InvestigationViewReadOnly
                    investigation={incidentDetail?.investigation}
                  />
                </Section>
              );
            })()}

            {/* Closure / Review – Manager/SP Admin only when incident is closed or reported */}
            {(() => {
              const canShowClosure =
                roleAccess(
                  [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager],
                  PERMISSION_KEYS.INCIDENT_MANAGEMENT,
                  PERMISSION_KEYS_CHILDREN.INCIDENT_MANAGEMENT.INVESTIGATE,
                ) &&
                [
                  INCIDENT_STATUS.CLOSED,
                  INCIDENT_STATUS.REPORTED,
                ].includes(incidentDetail?.status) &&
                incidentDetail?.closure?.data;
              if (!canShowClosure) return null;
              return (
                <Section
                  title="Closure & Review"
                  className="mb-4"
                  icon={<FiCheckCircle className="w-4 h-4" />}
                >
                  <ClosureViewReadOnly
                    closure={incidentDetail?.closure}
                    ndisReferenceNumber={incidentDetail?.ndis_reference_number}
                  />
                </Section>
              );
            })()}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <Section
                title={
                  incidentDetail.attachments?.length > 0
                    ? `Attachments (${incidentDetail.attachments.length})`
                    : "Attachments"
                }
                className="mb-0"
                onEdit={sectionEdit("attachments")}
              >
                {incidentDetail.attachments?.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {incidentDetail.attachments.map((attachment, index) => (
                      <a
                        key={index}
                        href={
                          typeof attachment === "string"
                            ? attachment
                            : attachment.url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors group"
                      >
                        <FiFileText className="w-5 h-5 text-gray-400 mr-2 group-hover:text-blue-500" />
                        <span className="flex-1 text-sm text-gray-700 truncate">
                          {attachment.name || `File ${index + 1}`}
                        </span>
                        <FiDownload className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FiFileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No attachments</p>
                  </div>
                )}
              </Section>

              {incidentDetail.signature && (
                <Section
                  title="Signature"
                  className="mb-0"
                  onEdit={sectionEdit("attachments")}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <img
                        src={incidentDetail.signature}
                        alt="Signature"
                        className="h-16 border border-gray-200 rounded-lg bg-white p-2"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Signed By
                      </p>
                      <p className="text-sm font-medium text-gray-800">
                        {incidentDetail.signatory_name ||
                          incidentDetail.reported_by?.name ||
                          incidentDetail.created_by?.name ||
                          "-"}
                      </p>
                      {incidentDetail.submitted_at && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {moment(incidentDetail.submitted_at).format(
                            "DD/MM/YYYY hh:mm A",
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </Section>
              )}
            </div>

            {incidentDetail.activities?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                <div
                  className="px-5 py-3 border-b border-gray-100 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setActivityExpanded(!activityExpanded)}
                >
                  <div className="flex items-center gap-2">
                    <FiClock className="w-4 h-4 text-gray-400" />
                    <h2 className="text-base font-semibold text-gray-800">
                      Activity Timeline
                    </h2>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      {incidentDetail.activities.length}{" "}
                      {incidentDetail.activities.length === 1
                        ? "Event"
                        : "Events"}
                    </span>
                  </div>
                  <span className="text-gray-400">
                    {activityExpanded ? (
                      <FiChevronUp className="w-5 h-5" />
                    ) : (
                      <FiChevronDown className="w-5 h-5" />
                    )}
                  </span>
                </div>

                {activityExpanded && (
                  <div className="p-5">
                    <div className="flow-root">
                      <ul className="-mb-8">
                        {incidentDetail.activities.map((activity, idx) => {
                          const metadata = parseMetadata(activity);
                          const isLast =
                            idx === incidentDetail.activities.length - 1;
                          return (
                            <li key={idx}>
                              <div className="relative pb-8">
                                {!isLast && (
                                  <span
                                    className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                                    aria-hidden="true"
                                  />
                                )}
                                <div className="relative flex items-start space-x-3">
                                  <div className="relative">
                                    <span
                                      className={classNames(
                                        "h-10 w-10 rounded-full flex items-center justify-center ring-4 ring-white",
                                        getActivityBgColor(
                                          activity.activity_type,
                                          metadata,
                                        ),
                                      )}
                                    >
                                      {getActivityIcon(
                                        activity.activity_type,
                                        metadata,
                                      )}
                                    </span>
                                  </div>

                                  <div className="flex-1 min-w-0 bg-gray-50 rounded-lg border border-gray-100 p-3">
                                    <div className="flex items-start justify-between gap-2">
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">
                                          {activity.description}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          {capitalizeWords(
                                            activity.user?.name ||
                                              activity.user?.email ||
                                              "System",
                                          )}
                                        </p>
                                      </div>
                                      <div className="text-right flex-shrink-0">
                                        <p className="text-xs font-medium text-gray-700">
                                          {moment(activity.created_at).format(
                                            "DD/MM/YYYY",
                                          )}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {moment(activity.created_at).format(
                                            "hh:mm A",
                                          )}
                                        </p>
                                      </div>
                                    </div>

                                    {metadata &&
                                      (metadata.step_name ||
                                        metadata.action ||
                                        metadata.status) && (
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                          {metadata.step_name && (
                                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">
                                              {metadata.step_name}
                                            </span>
                                          )}
                                          {metadata.action && (
                                            <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                                              {metadata.action ===
                                              "save_continue"
                                                ? "Save & Continue"
                                                : metadata.action ===
                                                    "create_draft"
                                                  ? "Created Draft"
                                                  : metadata.action === "submit"
                                                    ? "Submitted"
                                                    : capitalizeFirstLetter(
                                                        metadata.action.replace(
                                                          /_/g,
                                                          " ",
                                                        ),
                                                      )}
                                            </span>
                                          )}
                                          {metadata.status && (
                                            <span
                                              className={classNames(
                                                "text-xs px-2 py-0.5 rounded font-medium",
                                                metadata.status === "submitted"
                                                  ? "bg-green-100 text-green-700"
                                                  : "bg-yellow-100 text-yellow-700",
                                              )}
                                            >
                                              {capitalizeFirstLetter(
                                                metadata.status,
                                              )}
                                            </span>
                                          )}
                                        </div>
                                      )}

                                    {metadata?.changes &&
                                      Object.keys(metadata.changes).length >
                                        0 && (
                                        <div className="mt-3 pt-2 border-t border-gray-200">
                                          <p className="text-xs font-semibold text-gray-600 mb-1.5">
                                            Changes Made:
                                          </p>
                                          <div className="space-y-1">
                                            {Object.entries(
                                              metadata.changes,
                                            ).map(([key, change]) => (
                                              <div
                                                key={key}
                                                className="text-xs"
                                              >
                                                {key === "workers" ||
                                                key === "participants" ? (
                                                  <div className="flex items-center gap-2">
                                                    <span className="font-medium text-gray-700">
                                                      {capitalizeFirstLetter(
                                                        key,
                                                      )}
                                                      :
                                                    </span>
                                                    {change.added?.length >
                                                      0 && (
                                                      <span className="text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                                                        +{change.added.length}{" "}
                                                        Added
                                                      </span>
                                                    )}
                                                    {change.removed?.length >
                                                      0 && (
                                                      <span className="text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                                                        -{change.removed.length}{" "}
                                                        Removed
                                                      </span>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <div className="flex items-center flex-wrap gap-1">
                                                    <span className="font-medium text-gray-700">
                                                      {capitalizeFirstLetter(
                                                        key.replace(/_/g, " "),
                                                      )}
                                                      :
                                                    </span>
                                                    <span className="text-red-500 line-through bg-red-50 px-1.5 py-0.5 rounded">
                                                      {change.from || "(Empty)"}
                                                    </span>
                                                    <span className="text-gray-400">
                                                      →
                                                    </span>
                                                    <span className="text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                                                      {change.to || "(Empty)"}
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      <ModalSkeleton
        isOpen={showInvestigateModal}
        closeModal={() => setShowInvestigateModal(false)}
        title="Incident Investigation"
        cssClass="w-full max-w-5xl mx-4"
        bodyPadding="p-0"
      >
        <InvestigationForm
          incident={incidentDetail}
          onSubmit={(data) => {
            if (isUserGracePeriodOver) return;
            investigateIncidentCall({
              uuid,
              ...data,
            });
          }}
          onSaveDraft={(data) => {
            if (isUserGracePeriodOver) return;
            investigateIncidentCall({
              uuid,
              ...data,
              is_draft: true,
            });
          }}
          onCancel={() => setShowInvestigateModal(false)}
          isLoading={investigateLoading}
          initialData={incidentDetail?.investigation?.data || null}
          readOnly={false}
        />
      </ModalSkeleton>

      <ModalSkeleton
        isOpen={showCloseModal}
        closeModal={() => setShowCloseModal(false)}
        title="Incident Review & Close"
        cssClass="w-full max-w-4xl mx-4"
        bodyPadding="p-0"
      >
        <ReviewCloseForm
          incident={incidentDetail}
          investigation={incidentDetail?.investigation || null}
          onSubmit={(data) => {
            if (isUserGracePeriodOver) return;
            closeIncidentCall({
              uuid,
              ...data,
            });
          }}
          onSaveDraft={(data) => {
            if (isUserGracePeriodOver) return;
            closeIncidentDraftCall({
              uuid,
              ...data,
            });
          }}
          onCancel={() => setShowCloseModal(false)}
          isLoading={closeLoading || closeDraftLoading}
          initialData={
            incidentDetail?.closure
              ? {
                  ...incidentDetail.closure.data,
                  reported_to_commission_at:
                    incidentDetail.closure.reported_to_commission_at,
                  ndis_reference_number: incidentDetail.ndis_reference_number,
                }
              : null
          }
          readOnly={false}
        />
      </ModalSkeleton>
    </div>
  );
};

const mapStateToProps = (state) => ({
  incidentDetail: getIncidentDetail(state),
  detailLoading: getIncidentDetailLoading(state),
  investigateLoading: getIncidentInvestigateLoading(state),
  investigateSuccess: getIncidentInvestigateSuccess(state),
  closeLoading: getIncidentCloseLoading(state),
  closeSuccess: getIncidentCloseSuccess(state),
  closeDraftLoading: getIncidentCloseDraftLoading(state),
  closeDraftSuccess: getIncidentCloseDraftSuccess(state),
});

const mapDispatchToProps = {
  getIncidentDetailCall: incidentActions.getIncidentDetailRequest,
  investigateIncidentCall: incidentActions.investigateIncidentRequest,
  closeIncidentCall: incidentActions.closeIncidentRequest,
  closeIncidentDraftCall: incidentActions.closeIncidentDraftRequest,
  resetInvestigateCall: incidentActions.resetIncidentInvestigate,
  resetCloseCall: incidentActions.resetIncidentClose,
  resetCloseDraftCall: incidentActions.resetIncidentCloseDraft,
};

export default connect(mapStateToProps, mapDispatchToProps)(ViewIncident);
